/**
 * Output Aggregator Utility
 *
 * Aggregates outputs from all steps in a plan for display in the Outputs tab.
 * Collects from multiple sources:
 * - step.executionResults[].output (raw automation output)
 * - step.context?.collectedData (structured extracted output)
 * - step.intake?.normalizedResult (user intake results)
 * - step.assessment?.extractedFacts (AI-extracted facts)
 */

import { Plan, PlanStep, ExecutionResult } from '../types/generated/graphql';

/**
 * Source of the output data
 */
export type OutputSource = 'automation' | 'manual' | 'intake' | 'assessment';

/**
 * Type of output based on step intent or data type
 */
export type OutputType = 
  | 'research'
  | 'email'
  | 'schedule'
  | 'calculation'
  | 'document'
  | 'intake'
  | 'assessment'
  | 'other';

/**
 * A single output entry aggregated from a step
 */
export interface OutputEntry {
  /** ID for React keys */
  id: string;
  /** Step this output came from */
  stepId: string;
  /** Title of the step */
  stepTitle: string;
  /** Phase the step belongs to */
  phaseId: string;
  /** Phase title for display */
  phaseTitle: string;
  /** Phase order for sorting */
  phaseOrder: number;
  /** Step order within phase */
  stepOrder: number;
  /** Source of the output */
  source: OutputSource;
  /** Type of output */
  type: OutputType;
  /** When this output was created */
  timestamp: string;
  /** Structured data from the output */
  data: Record<string, unknown>;
  /** Raw output from execution results (for transparency) */
  rawOutput?: unknown;
  /** Whether the step is completed */
  isCompleted: boolean;
}

/**
 * Map step intent to output type
 */
function intentToOutputType(intent?: string | null): OutputType {
  switch (intent) {
    case 'RESEARCH':
      return 'research';
    case 'SEND_NOTIFICATION':
      return 'email';
    case 'SCHEDULE':
      return 'schedule';
    case 'CALCULATION':
      return 'calculation';
    case 'COLLECT_DOCUMENT':
    case 'SEND_SIGNATURE':
      return 'document';
    default:
      return 'other';
  }
}

/**
 * Extract outputs from a single step
 */
function extractStepOutputs(
  step: PlanStep,
  phaseMap: Map<string, { title: string; order: number }>,
  stepIndex: number
): OutputEntry[] {
  const outputs: OutputEntry[] = [];
  const phaseInfo = phaseMap.get(step.phaseId || '') || { title: 'Unknown Phase', order: 999 };
  const isCompleted = step.status === 'COMPLETED';
  const baseId = `${step.id}`;

  // 1. Extract from collectedData (structured automation output)
  const collectedData = step.context?.collectedData as Record<string, unknown> | undefined;
  if (collectedData && typeof collectedData === 'object' && Object.keys(collectedData).length > 0) {
    // Filter out metadata fields
    const { extractedAt, ...data } = collectedData;
    if (Object.keys(data).length > 0) {
      outputs.push({
        id: `${baseId}-collected`,
        stepId: step.id,
        stepTitle: step.title,
        phaseId: step.phaseId || '',
        phaseTitle: phaseInfo.title,
        phaseOrder: phaseInfo.order,
        stepOrder: stepIndex,
        source: 'automation',
        type: intentToOutputType(step.intent),
        timestamp: (extractedAt as string) || new Date().toISOString(),
        data,
        isCompleted,
      });
    }
  }

  // 2. Extract from executionResults (raw output)
  const executionResults = step.executionResults as ExecutionResult[] | undefined;
  if (executionResults && executionResults.length > 0) {
    const successfulResults = executionResults.filter(
      (r) => r.status === 'SUCCESS' && r.output !== null && r.output !== undefined
    );

    if (successfulResults.length > 0 && !collectedData) {
      // Only add raw outputs if we don't have structured collectedData
      // to avoid duplication
      for (const result of successfulResults) {
        outputs.push({
          id: `${baseId}-exec-${result.actionId}`,
          stepId: step.id,
          stepTitle: step.title,
          phaseId: step.phaseId || '',
          phaseTitle: phaseInfo.title,
          phaseOrder: phaseInfo.order,
          stepOrder: stepIndex,
          source: 'automation',
          type: intentToOutputType(step.intent),
          timestamp: result.completedAt,
          data: typeof result.output === 'object' ? (result.output as Record<string, unknown>) : { value: result.output },
          rawOutput: result.output,
          isCompleted,
        });
      }
    } else if (successfulResults.length > 0) {
      // Add raw output reference to the collectedData entry if it exists
      const existingEntry = outputs.find((o) => o.id === `${baseId}-collected`);
      if (existingEntry) {
        existingEntry.rawOutput = successfulResults.map((r) => r.output);
      }
    }
  }

  // 3. Extract from intake normalizedResult
  const intakeResult = step.intake?.normalizedResult as Record<string, unknown> | undefined;
  if (intakeResult && typeof intakeResult === 'object' && Object.keys(intakeResult).length > 0) {
    outputs.push({
      id: `${baseId}-intake`,
      stepId: step.id,
      stepTitle: step.title,
      phaseId: step.phaseId || '',
      phaseTitle: phaseInfo.title,
      phaseOrder: phaseInfo.order,
      stepOrder: stepIndex,
      source: 'intake',
      type: 'intake',
      timestamp: new Date().toISOString(),
      data: intakeResult,
      isCompleted,
    });
  }

  // 4. Extract from assessment extractedFacts
  const assessmentFacts = step.assessment?.extractedFacts as Record<string, unknown> | undefined;
  if (assessmentFacts && typeof assessmentFacts === 'object' && Object.keys(assessmentFacts).length > 0) {
    outputs.push({
      id: `${baseId}-assessment`,
      stepId: step.id,
      stepTitle: step.title,
      phaseId: step.phaseId || '',
      phaseTitle: phaseInfo.title,
      phaseOrder: phaseInfo.order,
      stepOrder: stepIndex,
      source: 'assessment',
      type: 'assessment',
      timestamp: new Date().toISOString(),
      data: assessmentFacts,
      isCompleted,
    });
  }

  return outputs;
}

/**
 * Aggregate all outputs from a plan
 *
 * @param plan - The plan to aggregate outputs from
 * @returns Array of output entries sorted by phase order, then step order
 */
export function aggregatePlanOutputs(plan: Plan): OutputEntry[] {
  // Build phase map for quick lookup
  const phaseMap = new Map<string, { title: string; order: number }>();
  for (const phase of plan.phases) {
    phaseMap.set(phase.id, { title: phase.title, order: phase.order });
  }

  // Extract outputs from all steps
  const allOutputs: OutputEntry[] = [];
  plan.steps.forEach((step, index) => {
    const stepOutputs = extractStepOutputs(step as PlanStep, phaseMap, index);
    allOutputs.push(...stepOutputs);
  });

  // Sort by phase order, then step order, then timestamp
  allOutputs.sort((a, b) => {
    if (a.phaseOrder !== b.phaseOrder) return a.phaseOrder - b.phaseOrder;
    if (a.stepOrder !== b.stepOrder) return a.stepOrder - b.stepOrder;
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  return allOutputs;
}

/**
 * Group outputs by phase
 */
export function groupOutputsByPhase(
  outputs: OutputEntry[]
): Map<string, { phaseTitle: string; phaseOrder: number; outputs: OutputEntry[] }> {
  const groups = new Map<string, { phaseTitle: string; phaseOrder: number; outputs: OutputEntry[] }>();

  for (const output of outputs) {
    const existing = groups.get(output.phaseId);
    if (existing) {
      existing.outputs.push(output);
    } else {
      groups.set(output.phaseId, {
        phaseTitle: output.phaseTitle,
        phaseOrder: output.phaseOrder,
        outputs: [output],
      });
    }
  }

  return groups;
}

/**
 * Group outputs by step
 */
export function groupOutputsByStep(
  outputs: OutputEntry[]
): Map<string, { stepTitle: string; outputs: OutputEntry[] }> {
  const groups = new Map<string, { stepTitle: string; outputs: OutputEntry[] }>();

  for (const output of outputs) {
    const existing = groups.get(output.stepId);
    if (existing) {
      existing.outputs.push(output);
    } else {
      groups.set(output.stepId, {
        stepTitle: output.stepTitle,
        outputs: [output],
      });
    }
  }

  return groups;
}

/**
 * Group outputs by type
 */
export function groupOutputsByType(
  outputs: OutputEntry[]
): Map<OutputType, OutputEntry[]> {
  const groups = new Map<OutputType, OutputEntry[]>();

  for (const output of outputs) {
    const existing = groups.get(output.type);
    if (existing) {
      existing.push(output);
    } else {
      groups.set(output.type, [output]);
    }
  }

  return groups;
}

/**
 * Filter outputs by source
 */
export function filterOutputsBySource(
  outputs: OutputEntry[],
  sources: OutputSource[]
): OutputEntry[] {
  if (sources.length === 0) return outputs;
  return outputs.filter((o) => sources.includes(o.source));
}

/**
 * Filter outputs by type
 */
export function filterOutputsByType(
  outputs: OutputEntry[],
  types: OutputType[]
): OutputEntry[] {
  if (types.length === 0) return outputs;
  return outputs.filter((o) => types.includes(o.type));
}

/**
 * Search outputs by text query
 */
export function searchOutputs(
  outputs: OutputEntry[],
  query: string
): OutputEntry[] {
  if (!query.trim()) return outputs;

  const lowerQuery = query.toLowerCase();
  return outputs.filter((output) => {
    // Search in step title
    if (output.stepTitle.toLowerCase().includes(lowerQuery)) return true;

    // Search in phase title
    if (output.phaseTitle.toLowerCase().includes(lowerQuery)) return true;

    // Search in data values
    const dataStr = JSON.stringify(output.data).toLowerCase();
    if (dataStr.includes(lowerQuery)) return true;

    return false;
  });
}

/**
 * Get output statistics for a plan
 */
export function getOutputStats(outputs: OutputEntry[]): {
  total: number;
  bySource: Record<OutputSource, number>;
  byType: Record<OutputType, number>;
  completed: number;
} {
  const stats = {
    total: outputs.length,
    bySource: {
      automation: 0,
      manual: 0,
      intake: 0,
      assessment: 0,
    } as Record<OutputSource, number>,
    byType: {
      research: 0,
      email: 0,
      schedule: 0,
      calculation: 0,
      document: 0,
      intake: 0,
      assessment: 0,
      other: 0,
    } as Record<OutputType, number>,
    completed: 0,
  };

  for (const output of outputs) {
    stats.bySource[output.source]++;
    stats.byType[output.type]++;
    if (output.isCompleted) stats.completed++;
  }

  return stats;
}

